import { z } from 'zod';
import { CAST_REGISTRY } from './cast.js';
/**
 * What a rewrite changed — GEN-56.
 *
 * The console reported a regeneration's *status* and nothing about its *result*, so the
 * reviewer re-read the whole scene and tried to remember what it had said. An instruction
 * like "make the third option less obviously wrong" either did that or did something
 * else, and the only way to tell was a memory test.
 *
 * Computed over the **planned** shape rather than the row, because that is the vocabulary
 * both sides of a regeneration already speak: `toPlannedScene` produces it from the scene
 * that existed, and the planner returns it for the scene that replaced it.
 */
/** One field a rewrite touched, in the words a reviewer reads. */
const changeSchema = z
    .strictObject({
    field: z.string().describe('Headline, Item 2, Explanation — not a column.'),
    before: z.string(),
    after: z.string(),
})
    .describe('One field, as it was and as it is.');
export const changesSchema = z
    .strictObject({
    changes: z.array(changeSchema),
    /** The planner's account of what it did, when the turn carried one back. */
    rationale: z.string().optional(),
})
    .describe('What one regeneration changed.');
/**
 * The comparable text, or nothing.
 *
 * Every field this reads is a string in `plannedSceneSchema`, so anything else is a shape
 * that has moved rather than a value that changed — and `String()` on an object produces
 * `[object Object]`, which would read as a difference on every comparison.
 */
const text = (value) => typeof value === 'string' ? value : '';
/**
 * Every field that differs, in reading order.
 *
 * Absent on one side and present on the other counts as a change — an item the rewrite
 * dropped is the kind of edit a reviewer most wants to see, and treating it as "no value,
 * no change" would hide exactly that.
 */
export function sceneChanges(before, after) {
    const was = (before ?? {});
    const now = (after ?? {});
    const fields = [
        ['Headline', was.prompt, now.prompt],
        ['Body', was.body, now.body],
        ['Question', was.question?.stem, now.question?.stem],
        ['Explanation', was.question?.explanation, now.question?.explanation],
    ];
    const wasItems = was.items ?? [];
    const nowItems = now.items ?? [];
    // The longer of the two, so an item that only one side has is still compared.
    for (let index = 0; index < Math.max(wasItems.length, nowItems.length); index += 1)
        fields.push([
            `Item ${String(index + 1)}`,
            wasItems[index]?.label,
            nowItems[index]?.label,
        ]);
    return fields
        .map(([field, a, b]) => ({ field, before: text(a), after: text(b) }))
        .filter((change) => change.before !== change.after);
}
/**
 * The changes a stored snapshot holds, or nothing — GEN-56, shared by SKILL-03.
 *
 * Derived on read rather than stored as a diff: the snapshot holds both sides, and a
 * computed answer cannot drift from them the way a second stored copy would.
 *
 * A snapshot that will not parse is treated as absent. It is JSON this system wrote, so a
 * failure means the shape moved between deploys, and a review screen is the wrong place to
 * raise that. Two callers — a regeneration row and an applied finding — write the same
 * `{ before, after }` payload, so they read it through the same function.
 */
export function changesIn(snapshot) {
    if (snapshot === null)
        return undefined;
    try {
        const { before, after } = JSON.parse(snapshot);
        return { changes: sceneChanges(before, after) };
    }
    catch {
        return undefined;
    }
}
/** JSON with keys sorted, so two objects holding the same values compare equal. */
const canonical = (value) => Array.isArray(value)
    ? value.map(canonical)
    : typeof value === 'object' && value !== null
        ? Object.fromEntries(Object.entries(value)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, one]) => [key, canonical(one)]))
        : value;
export const same = (a, b) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/**
 * Every leaf of a visual. Objects and arrays of objects (parties, lines, stakes) are walked;
 * anything else — a prop list, a gain list, a string — is one value, as a reader sees it.
 */
function leaves(value, path, into) {
    if (value === undefined || value === null)
        return;
    if (isRecord(value)) {
        for (const [key, one] of Object.entries(value))
            leaves(one, [...path, key], into);
        return;
    }
    if (Array.isArray(value) && value.some(isRecord)) {
        value.forEach((one, index) => {
            leaves(one, [...path, index], into);
        });
        return;
    }
    into.add(JSON.stringify(path));
}
export const valueAt = (value, path) => path.reduce((at, key) => isRecord(at) || Array.isArray(at)
    ? at[key]
    : undefined, value);
const shown = (value) => value === undefined
    ? ''
    : typeof value === 'string'
        ? value
        : Array.isArray(value) && value.every((one) => typeof one === 'string')
            ? value.join(', ')
            : typeof value === 'boolean'
                ? value
                    ? 'yes'
                    : 'no'
                : JSON.stringify(value);
const FIELD = {
    kind: 'Display',
    caption: 'Scene description',
    props: 'Props',
    awaiting: 'Whose line is next',
    by: 'side',
};
/** "Rosa, left" for a dialogue side, "Rosa" or "Person 2" for a seat in a situation. */
function partyName(before, after, path, fallback) {
    const character = valueAt(after, [...path, 'character']) ??
        valueAt(before, [...path, 'character']);
    return typeof character === 'string' && character in CAST_REGISTRY
        ? CAST_REGISTRY[character].name
        : fallback;
}
function label(before, after, path) {
    const [head, second, third] = path;
    const key = (one) => typeof one === 'string' ? (FIELD[one] ?? one) : String(one);
    if (head === 'left' || head === 'right') {
        const who = partyName(before, after, [head], '');
        return `${who === '' ? '' : `${who}, `}${head} side: ${key(second)}`;
    }
    if (typeof second === 'number') {
        const seat = String(second + 1);
        const where = head === 'cast'
            ? partyName(before, after, ['cast', second], `Person ${seat}`)
            : head === 'lines'
                ? `Line ${seat}`
                : `${key(head)} ${seat}`;
        return third === undefined ? where : `${where}: ${key(third)}`;
    }
    return path.map(key).join(': ');
}
/**
 * Every field of a scene's visual that differs, in the words a reviewer reads — GEN-135.
 * Each carries its path, so a revert can put back exactly these fields and no others.
 */
export function visualChanges(before, after) {
    const paths = new Set();
    leaves(before, [], paths);
    leaves(after, [], paths);
    return [...paths].flatMap((key) => {
        const path = JSON.parse(key);
        const was = valueAt(before, path);
        const now = valueAt(after, path);
        return same(was, now)
            ? []
            : [
                {
                    path,
                    field: label(before, after, path),
                    before: shown(was),
                    after: shown(now),
                },
            ];
    });
}
//# sourceMappingURL=changes.js.map